import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { announcementsAPI, sectionsAPI } from '../services/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { BackHeader } from '../components/BackHeader';

interface Announcement {
  id: string;
  title: string;
  content: string;
  category: 'general' | 'maintenance' | 'financial' | 'security' | 'social';
  priority: 'normal' | 'high' | 'urgent';
  author: string;
  publishedAt: string;
  // The backend table stores the timestamp as `created_at` (and sometimes
  // `published_at`) in snake_case; keep them optional so we can fall back to
  // them when `publishedAt` is absent.
  created_at?: string;
  published_at?: string;
  expiresAt?: string;
  attachments?: { name: string; url: string }[];
  readCount: number;
  targetRoles: string[];
  section_ids?: string[];
}

const categoryColors: Record<string, string> = {
  general: 'bg-blue-500/20 text-blue-400',
  maintenance: 'bg-yellow-500/20 text-yellow-400',
  financial: 'bg-green-500/20 text-green-400',
  security: 'bg-red-500/20 text-red-400',
  social: 'bg-purple-500/20 text-purple-400'
};

const categoryLabels: Record<string, string> = {
  general: 'General',
  maintenance: 'Mantenimiento',
  financial: 'Financiero',
  security: 'Seguridad',
  social: 'Social'
};

// Recursively search for an array of announcement-like objects in a nested response.
function extractAnnouncements(raw: any): Announcement[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  // Common nested keys where the list might live
  for (const key of ['data', 'announcements', 'items', 'results']) {
    if (raw[key] !== undefined) {
      const found = extractAnnouncements(raw[key]);
      if (found.length > 0) return found;
    }
  }
  return [];
}

export default function Announcements() {
  // D6 fix (2026-10-01, QA): writes must target the condo being MANAGED
  // (activeCondoId from the condo switcher), not the active-context condo.
  const { user, isAdmin, activeCondoId, setActiveCondo, contexts, memberships } = useAuth();
  const { success, error } = useToast();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | Announcement['category']>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);
  const [sectionIds, setSectionIds] = useState<string[]>([]);
  
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    category: 'general' as Announcement['category'],
    priority: 'normal' as Announcement['priority'],
    expiresAt: ''
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      // D6 fix (2026-10-01, QA): list the condo being MANAGED (activeCondoId),
      // not localStorage user.condoId (the active-context condo).
      const condoId = activeCondoId || user?.condoId;
      if (!condoId) {
        setAnnouncements([]);
        return;
      }
      const response = await announcementsAPI.getAll(condoId);
      try {
        const sectionResponse = await sectionsAPI.getAll(condoId);
        const sectionData = sectionResponse.data?.data?.sections ?? sectionResponse.data?.sections ?? [];
        setSections(Array.isArray(sectionData) ? sectionData : []);
      } catch { setSections([]); }
      // The API may return the list at response.data, response.data.data,
      // response.data.announcements, or other nested shapes.
      // Use a recursive extractor to find the array.
      const extracted = extractAnnouncements(response.data);
      setAnnouncements(extracted);
    } catch (err) {
      console.error('Error loading announcements:', err);
      // Fallback to empty array on error
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // D6 fix (2026-10-01, QA): publish to the condo being MANAGED.
      const condoId = activeCondoId || user?.condoId;
      if (!condoId) {
        error('Selecciona el condominio que vas a administrar antes de publicar');
        return;
      }
      await announcementsAPI.create(condoId, {
        ...newAnnouncement,
        section_ids: sectionIds,
        author: user?.name || 'Administración',
        publishedAt: new Date().toISOString(),
      });
      success('Anuncio publicado exitosamente');
      setShowCreateModal(false);
      setSectionIds([]);
      setNewAnnouncement({
        title: '',
        content: '',
        category: 'general',
        priority: 'normal',
        expiresAt: ''
      });
      loadAnnouncements();
    } catch (err) {
      error('Error al publicar anuncio');
    }
  };

  const openEdit = (a: Announcement) => {
    setEditingAnnouncement(a);
    setSectionIds(a.section_ids ?? []);
    setNewAnnouncement({
      title: a.title,
      content: a.content,
      category: a.category,
      priority: a.priority,
      expiresAt: a.expiresAt ? String(a.expiresAt).slice(0, 16) : ''
    });
    setSelectedAnnouncement(null);
  };

  const closeForm = () => {
    setShowCreateModal(false);
    setEditingAnnouncement(null);
    setSectionIds([]);
    setNewAnnouncement({
      title: '',
      content: '',
      category: 'general',
      priority: 'normal',
      expiresAt: ''
    });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAnnouncement) return;
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await announcementsAPI.update(editingAnnouncement.id, {
        ...newAnnouncement,
        section_ids: sectionIds,
        author: editingAnnouncement.author || user.name || 'Administración',
        publishedAt: editingAnnouncement.publishedAt ?? editingAnnouncement.published_at ?? editingAnnouncement.created_at,
      });
      success('Anuncio actualizado exitosamente');
      closeForm();
      loadAnnouncements();
    } catch (err) {
      error('Error al actualizar el anuncio');
    }
  };

  const handleDelete = async (a: Announcement) => {
    if (!window.confirm(`¿Eliminar el anuncio "${a.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await announcementsAPI.delete(a.id);
      success('Anuncio eliminado');
      setSelectedAnnouncement(null);
      setAnnouncements(prev => prev.filter(x => x.id !== a.id));
      loadAnnouncements();
    } catch (err) {
      error('Error al eliminar el anuncio');
    }
  };

  const filteredAnnouncements = filter === 'all' 
    ? announcements 
    : announcements.filter(a => a.category === filter);

  if (loading) {
    return (
      <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-surface-3)]">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-[var(--v-text-secondary)] mt-4">Cargando anuncios...</p>
        </div>
      </div>
    );
  }

  // D6 fix (2026-10-01, QA): operate on the condo being MANAGED
  // (activeCondoId), with a visible label. If no managed condo can be
  // resolved, block and offer an explicit selector instead of silently
  // defaulting to the active-context condo.
  const managedCondoId = activeCondoId || user?.condoId || '';
  const managedCondoLabel = (() => {
    if (!managedCondoId) return '';
    const ctx = (contexts || []).find((c) => c.kind === 'condo' && c.condoId === managedCondoId);
    if (ctx?.label) return ctx.label;
    const m = (memberships || []).find((mm) => mm.condoId === managedCondoId);
    return m?.condoName || managedCondoId;
  })();
  const condoOptions = Array.from(new Set([
    ...(contexts || []).filter((c) => c.kind === 'condo' && c.condoId).map((c) => c.condoId as string),
    ...(memberships || []).map((mm) => mm.condoId).filter(Boolean),
  ]));

  if (!managedCondoId) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">📢 Anuncios</h2>
          <p className="text-[var(--v-text-secondary)]">Comunicados oficiales del condominio</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-12 rounded-xl border border-[var(--v-surface-3)] text-center">
          <div className="text-6xl mb-4">🏢</div>
          <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">{condoOptions.length > 0 ? 'Selecciona un condominio' : 'Sin condominio asignado'}</h3>
          <p className="text-[var(--v-text-secondary)]">{condoOptions.length > 0 ? 'Elige el condominio que vas a administrar para publicar anuncios.' : 'Aún no se te ha asignado un condominio. Contacta al administrador para continuar.'}</p>
          {condoOptions.length > 0 && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {condoOptions.map((id) => {
                const c = (contexts || []).find((x) => x.kind === 'condo' && x.condoId === id);
                const m = (memberships || []).find((mm) => mm.condoId === id);
                return (
                  <button key={id} onClick={() => setActiveCondo(id)}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors">
                    {c?.label || m?.condoName || id}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackHeader fallback="/" />
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">📢 Anuncios</h2>
          <p className="text-[var(--v-text-secondary)]">Comunicados oficiales del condominio</p>
        </div>
        {isAdmin() && (
          <button
            onClick={() => { closeForm(); setShowCreateModal(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition"
          >
            + Nuevo Anuncio
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'all' ? 'bg-blue-600 text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-primary)] hover:bg-[var(--v-surface-3)]'
          }`}
        >
          Todos
        </button>
        {Object.entries(categoryLabels).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key as Announcement['category'])}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === key ? 'bg-blue-600 text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-primary)] hover:bg-[var(--v-surface-3)]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        {filteredAnnouncements.length === 0 ? (
          <div className="bg-[var(--v-surface-1)] p-12 rounded-xl border border-[var(--v-surface-3)] text-center">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-[var(--v-text-secondary)]">No hay anuncios en esta categoría</p>
          </div>
        ) : (
          filteredAnnouncements.map((announcement) => (
            <div
              key={announcement.id}
              onClick={() => setSelectedAnnouncement(announcement)}
              className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-surface-3)] hover:border-slate-600 cursor-pointer transition"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs ${categoryColors[announcement.category]}`}>
                      {categoryLabels[announcement.category]}
                    </span>
                    {announcement.priority === 'urgent' && (
                      <span className="bg-red-600 text-white px-2 py-1 rounded text-xs">
                        URGENTE
                      </span>
                    )}
                    {announcement.priority === 'high' && (
                      <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs">
                        IMPORTANTE
                      </span>
                    )}
                  </div>
                  <div className="mb-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]">
                      {announcement.section_ids?.length ? announcement.section_ids.map(id => sections.find(s => s.id === id)?.name || id).join(', ') : 'Todo el condominio'}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">{announcement.title}</h3>
                  <p className="text-[var(--v-text-secondary)] text-sm line-clamp-2">{announcement.content}</p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-[var(--v-text-secondary)]">
                    <span>👤 {announcement.author}</span>
                    <span>📅 {formatDate(announcement.publishedAt ?? announcement.published_at ?? announcement.created_at)}</span>
                    <span>👁️ {announcement.readCount ?? 0} lecturas</span>
                  </div>
                  {isAdmin() && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEdit(announcement); }}
                        className="bg-[var(--v-surface-2)] hover:bg-[var(--v-surface-3)] text-[var(--v-text-primary)] px-3 py-1.5 rounded-lg text-sm font-medium transition"
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(announcement); }}
                        className="bg-red-600/10 hover:bg-red-600/20 text-red-400 px-3 py-1.5 rounded-lg text-sm font-medium transition"
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingAnnouncement) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-[var(--v-text-primary)] mb-4">
              {editingAnnouncement ? 'Editar Anuncio' : 'Nuevo Anuncio'}
            </h3>
            {managedCondoId && (
              <div className="mb-4 flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <span>📍 Publicando en:</span>
                <span className="font-medium">{managedCondoLabel || managedCondoId}</span>
              </div>
            )}
            <form onSubmit={editingAnnouncement ? handleUpdate : handleCreate} className="space-y-4">
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Título *</label>
                <input
                  type="text"
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                  className="w-full bg-[var(--v-surface-2)] border border-[var(--v-surface-3)] rounded-lg px-4 py-3 text-[var(--v-text-primary)]"
                  required
                />
              </div>

              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Contenido *</label>
                <textarea
                  value={newAnnouncement.content}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                  rows={5}
                  className="w-full bg-[var(--v-surface-2)] border border-[var(--v-surface-3)] rounded-lg px-4 py-3 text-[var(--v-text-primary)]"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Categoría</label>
                  <select
                    value={newAnnouncement.category}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, category: e.target.value as Announcement['category'] })}
                    className="w-full bg-[var(--v-surface-2)] border border-[var(--v-surface-3)] rounded-lg px-4 py-3 text-[var(--v-text-primary)]"
                  >
                    {Object.entries(categoryLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Prioridad</label>
                  <select
                    value={newAnnouncement.priority}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, priority: e.target.value as Announcement['priority'] })}
                    className="w-full bg-[var(--v-surface-2)] border border-[var(--v-surface-3)] rounded-lg px-4 py-3 text-[var(--v-text-primary)]"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>
              </div>

              {sections.length > 0 && (
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Secciones</label>
                  <select multiple value={sectionIds} onChange={(e) => setSectionIds(Array.from(e.target.selectedOptions, option => option.value))}
                    className="w-full min-h-24 bg-[var(--v-surface-2)] border border-[var(--v-surface-3)] rounded-lg px-4 py-3 text-[var(--v-text-primary)]">
                    {sections.map(section => <option key={section.id} value={section.id}>{section.name}</option>)}
                  </select>
                  <p className="text-xs text-[var(--v-text-secondary)] mt-1">Sin selección: Todo el condominio</p>
                </div>
              )}

              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Fecha de Expiración (opcional)</label>
                <input
                  type="datetime-local"
                  value={newAnnouncement.expiresAt}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, expiresAt: e.target.value })}
                  className="w-full bg-[var(--v-surface-2)] border border-[var(--v-surface-3)] rounded-lg px-4 py-3 text-[var(--v-text-primary)]"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition"
                >
                  {editingAnnouncement ? 'Guardar Cambios' : 'Publicar Anuncio'}
                </button>
                <button
                  onClick={() => closeForm()}
                  className="flex-1 bg-[var(--v-surface-2)] hover:bg-[var(--v-surface-3)] text-[var(--v-text-primary)] py-3 rounded-lg font-medium transition"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {selectedAnnouncement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-2 py-1 rounded text-xs ${categoryColors[selectedAnnouncement.category]}`}>
                {categoryLabels[selectedAnnouncement.category]}
              </span>
              {selectedAnnouncement.priority !== 'normal' && (
                <span className={`px-2 py-1 rounded text-xs ${
                  selectedAnnouncement.priority === 'urgent' ? 'bg-red-600 text-white' : 'bg-orange-600 text-white'
                }`}>
                  {selectedAnnouncement.priority.toUpperCase()}
                </span>
              )}
            </div>
            
            <h3 className="text-xl font-semibold text-[var(--v-text-primary)] mb-4">{selectedAnnouncement.title}</h3>
            <p className="text-[var(--v-text-primary)] whitespace-pre-wrap mb-6">{selectedAnnouncement.content}</p>
            
            <div className="border-t border-[var(--v-surface-3)] pt-4 text-sm text-[var(--v-text-secondary)]">
              <p>Publicado por: {selectedAnnouncement.author}</p>
              <p>Fecha: {formatDateTime(selectedAnnouncement.publishedAt ?? selectedAnnouncement.published_at ?? selectedAnnouncement.created_at)}</p>
              <p>Lecturas: {selectedAnnouncement.readCount}</p>
            </div>

            <div className="mt-6 flex gap-2">
              {isAdmin() && (
                <>
                  <button
                    onClick={() => openEdit(selectedAnnouncement)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(selectedAnnouncement)}
                    className="flex-1 bg-red-600/10 hover:bg-red-600/20 text-red-400 py-3 rounded-lg font-medium transition"
                  >
                    Eliminar
                  </button>
                </>
              )}
              <button
                onClick={() => setSelectedAnnouncement(null)}
                className="flex-1 bg-[var(--v-surface-2)] hover:bg-[var(--v-surface-3)] text-[var(--v-text-primary)] py-3 rounded-lg font-medium transition"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
